import { FC, HTMLAttributes, ReactNode, useEffect, useLayoutEffect, useState } from "react";
import copy from "copy-to-clipboard";
import clx from "classnames";
import Button from "src/Components/Button";
import Input from "../Input/Input";
import EditableFieldToolbarButton from "./EditableFieldToolbarBautton/EditableFieldToolbarBautton";
import API from "src/API";
import { useOutsideClick } from "src/Hooks";
import UrlQualifier from "src/Utils/UrlQualifier";
import { ReactComponent as EditIcon } from "src/Themes/assets/Editing.svg";
import { ReactComponent as CopyIcon } from "src/Themes/assets/copy.svg"; 
import st from "./EditableField.module.scss";

interface IEditableFieldProps extends HTMLAttributes<HTMLDivElement> {
	type?: "text" | "textarea" | "number" | "custom";
	initValue?: string;
	placeholder?: string;
	readOnly?: boolean;
	allowCopy?: boolean;
    autoClose?: boolean;
    min?: boolean;
	onChangeValue: ( newValue: string ) => void;
	renderView?: ( props: any ) => ReactNode;
};

const EditableField: FC<IEditableFieldProps> = ( props ) => {

	const { type = "text", initValue = "", autoClose = true, min = false, readOnly = false, allowCopy = false, 
		placeholder, onChangeValue, renderView, 
		className, children, ...otherProps 
	} = props; 

	const ref = useOutsideClick<HTMLDivElement>( () => setEditMode(false) );
	const [ editMode, setEditMode ] = useState<boolean>(false);
	const [ value, setValue ] = useState<string>(initValue);

	useEffect( () => {
		setTimeout( () => setValue( initValue ), 300 );
	// eslint-disable-next-line
	}, [ editMode ] );

	// Следим за нажатиями Enter и Esc
	useLayoutEffect( () => {
		const onKeyUp = ( e: KeyboardEvent ) => {
			if ( e.code === "Enter" ) {
				onSaveChange();
			}
			else if ( e.code === "Escape" ) {
				onCloseEditMode();
			}
		};
		if ( !editMode ) {
			window.removeEventListener( "keyup", onKeyUp );
			return;
		}
		window.addEventListener( "keyup", onKeyUp );
		return () => {
			window.removeEventListener( "keyup", onKeyUp );
		};
	// eslint-disable-next-line
	}, [ editMode ] );

	// Копируем значение в буфер обмена
	const onCopyHandler = () => {
		const isSuccess = copy( value, {
			debug: window.isDev
		});
		const msg = isSuccess ? "Поле скопировано в буфер обмена" : "Ошибка при копировании значения поля в буфер обмена";
		API.showToast( isSuccess, msg );
	};

	// Сохраняем значение и выходим из режима редактирования...
	const onSaveChange = () => {
		if ( value !== initValue ) {
			onChangeValue( value );
            if ( autoClose ) {
                onCloseEditMode();
            }
		}
	}

	// Закрываем режим редактирования и сбрасываем значение до начального...
	const onCloseEditMode = () => setEditMode(false);

	// Рендерим режим редактирования
	const renderEditMode = () => {
		return (
			<div ref={ref} className={clx(st.editWrapper, st[type], className)}>
				<Input type={type} value={value} onChangeValue={ v => setValue(v) } />
				<div className={st.editToolbar}>
					<EditableFieldToolbarButton type="apply" onClick={onSaveChange} disable={(readOnly || !value || value === initValue)} />
					<EditableFieldToolbarButton type="cancel" onClick={onCloseEditMode} />
				</div>
			</div>
		);
	};

	// Рендерим режим просмотра
	const renderViewMode = () => (
		<div className={clx(st.viewWrapper, className )} {...otherProps}>
			{ 
				renderView
				? renderView(props) 
				: <div title={initValue} dangerouslySetInnerHTML={{ __html: UrlQualifier.getReformated(initValue) }} /> 
			}
			<div className={clx( st.viewToolbar, { [st.min]: min })}>
				{ 
					( allowCopy && value ) && (
						<Button size="min" outline onClick={() => onCopyHandler()}>
							<CopyIcon />
						</Button>
					)
				}
				<Button size="min" outline disable={readOnly} onClick={() => setEditMode(true) }>
					<EditIcon />
				</Button>
			</div>
		</div>
	);

	return editMode ? renderEditMode() : renderViewMode();
};



export default EditableField;
